import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Buttons } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import svgPaths from '../../../../imports/svg-knollny4nu';
import { useToast } from '@chakra-ui/react';
import {
  CustomCoinIcon,
  CustomHomeIcon,
  CustomMapIcon,
  CustomRankingIcon,
  CustomSettingsIcon,
} from '../../../../components/ui/CustomIcons';
import imgBackground from '../../../../assets/img/NewUI_Images/background.png';
import { useColor } from '../../../../components/ui/ColorContext';
import ColorPicker from '../../../../components/ui/ColorPicker';
import { Slider } from '../../../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../../../components/ui/dialog';
import { ScoreContext } from '../GamePreview';

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 255, 187'; // fallback to default green
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};

interface InteractionFutureThemeProps {
  data: any;
  options: any;
  optionClick: any;
  InteractionFunction: () => void;
  option: any;
  navTrack?: any;
  preloadedAssets: any;
  selectedPlayer: any;
  LastModiPrevData: any;
  RepeatSelectOption: any;
  RepeatPrevOption: any;
  contentByLanguage: any;
  currentScreenId?: number;
  gameInfo: any;
  animateInteraction?: boolean;
  handleValidate?: any;
  questState?: any;
  isInteractionButtonDisabled?: any;
  handleInteractionBackClick?: any;
  SetAudioOptions?: any;
  setPrevselectoptions: any;
  prevSelectOption: any;
  onNavigate?: (direction: 'left' | 'right') => void;
}

const InteractionFutureTheme: React.FC<InteractionFutureThemeProps> = ({
  setPrevselectoptions,
  prevSelectOption,
  data,
  isInteractionButtonDisabled,
  handleInteractionBackClick,
  option,
  options,
  optionClick,
  InteractionFunction,
  navTrack,
  preloadedAssets,
  selectedPlayer,
  LastModiPrevData,
  RepeatSelectOption,
  RepeatPrevOption,
  contentByLanguage,
  currentScreenId,
  gameInfo,
  animateInteraction,
  handleValidate,
  questState,
  SetAudioOptions,
  onNavigate,
}) => {
  const toast = useToast();
  const showGameToast = (
    toast: any,
    primaryColor: string,
    title: string,
    description?: string,
  ) => {
    toast({
      position: 'top',
      duration: 3000,
      isClosable: true,
      render: () => (
        <div
          style={{
            backdropFilter: 'blur(12px)',
            background: `linear-gradient(
            135deg,
            rgba(0,0,0,0.85),
            rgba(${hexToRgb(primaryColor)}, 0.25)
          )`,
            border: `1px solid ${primaryColor}`,
            boxShadow: `
            0 0 12px rgba(${hexToRgb(primaryColor)}, 0.6),
            inset 0 0 10px rgba(${hexToRgb(primaryColor)}, 0.4)
          `,
            borderRadius: '14px',
            padding: '14px 18px',
            color: '#fff',
            maxWidth: '360px',
          }}
        >
          <div
            style={{
              color: primaryColor,
              fontWeight: 600,
              fontSize: '0.95rem',
              textShadow: `0 0 8px rgba(${hexToRgb(primaryColor)}, 0.8)`,
            }}
          >
            {title}
          </div>

          {description && (
            <div
              style={{
                marginTop: '6px',
                fontSize: '0.85rem',
                opacity: 0.9,
              }}
            >
              {description}
            </div>
          )}
        </div>
      ),
    });
  };
  // ---------- copied / adapted state from old Interaction ----------
  const { primaryColor } = useColor();
  const [reapeatprevSelectOption, setRepeatselectoptions] = useState<any>();
  // const [prevSelectOption, setPrevselectoptions] = useState<any>([]);
  const { profile, setProfile } = useContext(ScoreContext);
  const [hasClicked, setHasClicked] = useState(false);

  // Local selection for this UI (A/B/C or qpOptions value)
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  useEffect(() => {
    if (RepeatSelectOption === true) {
      // setPrevselectoptions([]);
      if (RepeatPrevOption.length > 0) {
        const prevoptionseleted = RepeatPrevOption;

        // return;
        setRepeatselectoptions(prevoptionseleted);
      }
    } else {
      setRepeatselectoptions([]);
    }
  }, [RepeatSelectOption, RepeatPrevOption]);

  // ---------- Restore / initialize prev selection when question or profile changes ----------
  useEffect(() => {
    setPrevselectoptions(null);
    if (profile.score.length > 0 && !RepeatSelectOption) {
      let getOption: any = [];
      let updatedscore: any = [];

      if (questState?.[parseInt(profile?.currentQuest)] === 'Started') {
        getOption = profile?.score?.find(
          (item: any) => item?.seqId == data?.blockPrimarySequence,
        );
      } else if (
        questState?.[parseInt(profile?.currentQuest)] === 'replayallowed'
      ) {
        getOption = profile?.replayScore?.find(
          (item: any) => item?.seqId == data?.blockPrimarySequence,
        );
      }

      if (getOption) {
        const getFilterOption = options.filter(
          (item: any) => item?.qpOptions == getOption?.choosedoption,
        );
        setPrevselectoptions(getOption.choosedoption);
        SetAudioOptions(getFilterOption[0]);
        handleValidate(getFilterOption[0]);
      } else {
        // fallback to prop.option
        setPrevselectoptions(option);
      }
    } else {
      setPrevselectoptions(option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.blockPrimarySequence,
    options,
    questState,
    profile?.currentQuest,
    profile?.score,
    profile?.replayScore,
    RepeatSelectOption,
  ]);

  useEffect(() => {
    // Reset selectedOption when data changes (new interaction loaded)
    setSelectedOption(null);
    setHasClicked(false);

    // Check if this question is already answered
    const seq = data?.blockPrimarySequence;
    if (!seq) return;

    const existingScore =
      questState &&
      (questState[parseInt(profile?.currentQuest)] === 'replayallowed'
        ? profile?.replayScore?.find((p: any) => p.seqId === seq)
        : profile?.score?.find((p: any) => p.seqId === seq));

    // ✅ If score exists, restore it — do NOT clear
    if (existingScore) {
      setPrevselectoptions(existingScore.choosedoption);
      const matched = options.find(
        (o: any) => o.qpOptions === existingScore.choosedoption,
      );
      if (matched) {
        SetAudioOptions && SetAudioOptions(matched);
        handleValidate && handleValidate(matched);
      }
      return; // prevent any clearing below
    }

    // 🧹 If no saved score for this question, clear
    setPrevselectoptions(null);
    SetAudioOptions && SetAudioOptions(null);
  }, [
    data?.blockPrimarySequence,
    profile?.score?.length,
    profile?.replayScore?.length,
    questState,
    options,
  ]);
  useEffect(() => {
    if (hasClicked === true) {
      setHasClicked(false);
    }
    if (profile.score.length > 0 && !RepeatSelectOption) {
      let getOption: any = [];
      let updatedscore: any = [];
      if (questState[parseInt(profile?.currentQuest)] === 'Started') {
        // Remove matching object from profile?.score
        getOption = profile?.score?.find(
          (item: any) => item?.seqId == data?.blockPrimarySequence,
        );
      } else if (
        questState[parseInt(profile?.currentQuest)] === 'replayallowed'
      ) {
        // Remove matching object from profile?.replayScore
        getOption = profile?.replayScore?.find(
          (item: any) => item?.seqId == data?.blockPrimarySequence,
        );
      }
      if (getOption) {
        const getFilterOption = options.filter(
          (item: any) => item?.qpOptions == getOption?.choosedoption,
        );
        setPrevselectoptions(getOption.choosedoption);
        SetAudioOptions(getFilterOption[0]);
      } else {
        setPrevselectoptions(option);
      }
    } else {
      setPrevselectoptions(option);
    }
  }, [option]);

  // ---------- Navigation handlers ----------
  const isOptionRepeated = (opt: any) => {
    if (!RepeatSelectOption || !reapeatprevSelectOption) return false;
    return reapeatprevSelectOption.includes(opt);
  };

  // ✅ LEFT BUTTON LOGIC (OLD = handleInteractionBackClick(data))
  const handleLeft = () => {
    if (handleInteractionBackClick) {
      handleInteractionBackClick(data); // ## OLD LOGIC APPLIED
    } else {
      onNavigate && onNavigate('left');
    }
  };

  // ✅ RIGHT BUTTON LOGIC (OLD = hasClicked + InteractionFunction)
  const handleRight = () => {
    if (hasClicked) return; // ## OLD DOUBLE CLICK BLOCK
    setHasClicked(true); // ## OLD STATE

    InteractionFunction && InteractionFunction(); // ## OLD FUNCTION CALL

    onNavigate && onNavigate('right');
  };

  // ---------- sequenceMatches logic (kept from original) ----------
  const blockPrimarySequenceParts = data?.blockPrimarySequence?.split?.('.');
  const primaryKey = blockPrimarySequenceParts
    ? parseInt(blockPrimarySequenceParts[0])
    : null;

  const sequenceMatches =
    primaryKey !== null &&
    gameInfo?.blocks?.hasOwnProperty(primaryKey) &&
    data?.blockPrimarySequence ===
      gameInfo.blocks[primaryKey]?.['1']?.blockPrimarySequence;
  const playerHeight = document.getElementById('player');

  const blocknew = useMemo(() => {
    if (!reapeatprevSelectOption?.length) return false;

    const uniquePrevOptions = [...new Set(reapeatprevSelectOption)];
    const getoption = options.filter((item: any) =>
      uniquePrevOptions.includes(item.qpOptions),
    );

    return getoption.length > 0;
  }, [options, reapeatprevSelectOption]);
  const seqId = data?.blockPrimarySequence;

  const isAnswerSubmitted = Boolean(
    questState &&
      (questState[parseInt(profile?.currentQuest)] === 'replayallowed'
        ? profile?.replayScore?.some((s: any) => s.seqId === seqId) && !blocknew
        : profile?.score?.some((s: any) => s.seqId === seqId) && !blocknew),
  );

  // ---------- Render UI (UNCHANGED) ----------
  return (
    <div
      className="relative h-screen bg-cover bg-center bg-no-repeat" //   style={{ backgroundImage: `url('${imgBackground}')` }}
    >
      <div className="relative flex h-full w-full px-4 pb-4 sm:px-6 sm:pb-6 lg:px-8 lg:pb-8">
        {/* Interaction Content */}
        <div className="flex-grow-1 flex h-full flex-col items-start justify-center pt-24">
          <div
            data-aos="slide-right"
            data-aos-once="true"
            className="relative flex w-full max-w-xl flex-col items-center justify-start gap-5 rounded-[2.375rem] p-5 pb-6 backdrop-blur-[3.724rem] backdrop-filter"
            style={{
              background: `linear-gradient(21deg, rgba(0, 0, 0, 0.80) -10.76%, rgba(${hexToRgb(
                primaryColor,
              )}, 0.2) 127.18%)`,
            }}
          >
            {/* Interaction Title */}
            <div className="relative">
              <h1
                className="text-center text-4xl font-normal tracking-[0.005rem]"
                style={{
                  color: primaryColor,
                  textShadow: `rgba(${hexToRgb(
                    primaryColor,
                  )}, 0.5) 0px 0px 9px`,
                }}
              >
                Interaction
              </h1>
            </div>

            {/* Decorative Line */}
            <div className="relative flex w-full items-center justify-center">
              <div className="w-full">
                <div className="relative h-[1px] w-full">
                  <svg
                    className="block h-[1px] w-full"
                    fill="none"
                    preserveAspectRatio="none"
                    viewBox="0 0 533 1"
                  >
                    <line
                      stroke={`url(#paint0_linear_interaction_${primaryColor.replace(
                        '#',
                        '',
                      )})`}
                      x2="532.526"
                      y1="0.5"
                      y2="0.5"
                    />
                    <defs>
                      <linearGradient
                        gradientUnits="userSpaceOnUse"
                        id={`paint0_linear_interaction_${primaryColor.replace(
                          '#',
                          '',
                        )}`}
                        x1="532.526"
                        x2="0"
                        y1="0.999899"
                        y2="0.99992"
                      >
                        <stop stopColor={primaryColor} stopOpacity="0" />
                        <stop
                          offset="0.484375"
                          stopColor={primaryColor}
                          stopOpacity="0.994792"
                        />
                        <stop
                          offset="1"
                          stopColor={primaryColor}
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>

            {/* Question Section */}
            <div className="max-h-[450px] w-full overflow-y-auto">
              <div
                className="relative mb-6 flex w-full flex-row items-start justify-start gap-1.5 p-0 leading-[0]"
                style={{ color: primaryColor }}
              >
                {/* Q5 Badge */}
                <div className="relative flex w-[1.8rem] flex-row items-center justify-between p-0 shadow-[0px_0.993175px_3.9727px_0px_rgba(0,0,0,0.25)]">
                  <div className="text-shadow-[0px_7.48192px_18.1751px_rgba(0,0,0,0.46)] relative flex h-[0.688rem] w-[0.938rem] flex-col justify-center text-[1.316rem]">
                    <p className="block leading-[normal]">Q</p>
                  </div>
                  <div className="text-shadow-[0px_7.48192px_18.1751px_rgba(0,0,0,0.46)] relative flex h-[1.152rem] w-[0.715rem] flex-col justify-center text-[1.277rem]">
                    <p className="block leading-[normal]">s</p>
                  </div>
                </div>

                {/* Question Text */}
                <div className="font-rubik relative flex flex-col justify-center text-justify text-[1.185rem] font-medium tracking-[-0.0744148px]">
                  <p className="block leading-tight">
                    {contentByLanguage !== null
                      ? contentByLanguage
                      : data?.blockText}
                  </p>
                </div>
              </div>

              {/* Options Section */}
              <div className="flex w-full flex-col items-start justify-center gap-4">
                {options.map((item: any, index: number) => {
                  const qpOpt =
                    item.qpOptions ??
                    item.option ??
                    String.fromCharCode(65 + index);
                  const optText =
                    item.qpOptionText ?? item.text ?? item.label ?? '';

                  // ## OLD REPEAT LOGIC
                  const blocked =
                    reapeatprevSelectOption &&
                    reapeatprevSelectOption.some(
                      (prev: any) => prev === item.qpOptions,
                    );

                  return (
                    <div key={qpOpt} className="w-full">
                      <div
                        className="relative mb-4 w-full cursor-pointer rounded-[0.395rem] transition-all duration-200"
                        style={{
                          backgroundColor: blocked
                            ? 'rgba(128, 128, 128, 0.1)' // Gray background for repeated options
                            : prevSelectOption === item.qpOptions
                            ? `rgba(${hexToRgb(primaryColor)}, 0.04)`
                            : '',
                        }}
                        onClick={() => {
                          // 🚫 Lock only AFTER Next + Previous flow
                          if (isAnswerSubmitted) {
                            showGameToast(
                              toast,
                              primaryColor,
                              'Answer Locked',
                              'Your choice is locked in. You can try other paths during replay.',
                            );
                            return;
                          }

                          // 🔁 Repeat-selection restriction (keep existing logic)
                          if (
                            !reapeatprevSelectOption ||
                            !reapeatprevSelectOption.some(
                              (prev: any) => prev === item.qpOptions,
                            )
                          ) {
                            optionClick(item);
                          }
                        }}
                      >
                        <div className="relative flex h-full w-full flex-row items-center justify-center">
                          <div className="relative box-border flex w-full flex-row content-stretch items-center justify-center gap-2 px-2 py-1.5">
                            <div
                              className="font-rubik relative flex min-h-px min-w-px shrink-0 grow basis-0 flex-col justify-center text-justify text-[1.194rem] font-normal leading-none tracking-[-0.0744148px]"
                              // style={{

                              //   color:
                              //     prevSelectOption === item.qpOptions
                              //       ? primaryColor
                              //       : 'rgba(255,255,255,0.9)',

                              //   opacity: blocked ? 0.6 : 1,
                              // }}
                              style={{
                                color: blocked
                                  ? 'rgba(128, 128, 128, 0.6)' // Gray for repeated options
                                  : prevSelectOption === item.qpOptions
                                  ? primaryColor
                                  : 'rgba(255,255,255,0.9)',
                                opacity: blocked ? 0.5 : 1,
                              }}
                            >
                              <p className="block leading-tight">
                                {`${String.fromCharCode(65 + index)}) `}
                                {optText}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {index < options.length - 1 && (
                        <div className="relative flex w-full items-center justify-center">
                          <div className="w-full">
                            <div className="relative h-[1px] w-full">
                              <svg
                                className="block h-[1px] w-full rotate-180"
                                fill="none"
                                preserveAspectRatio="none"
                                viewBox="0 0 533 1"
                              >
                                <line
                                  stroke="url(#paint0_linear_divider)"
                                  x2="532.526"
                                  y1="0.5"
                                  y2="0.5"
                                />
                                <defs>
                                  <linearGradient
                                    gradientUnits="userSpaceOnUse"
                                    id="paint0_linear_divider"
                                    x1="0"
                                    x2="532.526"
                                    y1="1.5"
                                    y2="1.5"
                                  >
                                    <stop stopColor="white" />
                                    <stop offset="1" stopColor="#3D3D3D" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom Navigation */}
            {/* Bottom Navigation */}
            <div className="flex w-full items-center justify-between">
              {/* ## LEFT BUTTON — OLD LOGIC APPLIED */}
              {sequenceMatches ? (
                <div />
              ) : (
                <Buttons
                  variant="ghost"
                  size="sm"
                  onClick={handleLeft}
                  className="h-10 w-[3.75rem] rounded-3xl px-4 py-2 transition-all duration-200 hover:scale-110"
                  style={{
                    background: `linear-gradient(275.041deg, rgba(${hexToRgb(
                      primaryColor,
                    )}, 0.6) 7.25%, rgba(${hexToRgb(
                      primaryColor,
                    )}, 0.15) 84.803%)`,
                    border: `1px solid ${primaryColor}`,
                    boxShadow: `0px 0px 15.9542px 0px rgba(${hexToRgb(
                      primaryColor,
                    )}, 0.3)`,
                  }}
                >
                  <ChevronLeft className="!h-4 !w-4 text-white" />
                </Buttons>
              )}

              {/* ## RIGHT BUTTON — OLD CHAKRA CONDITION EXACT COPY */}
              {prevSelectOption !== null &&
                (!RepeatSelectOption ||
                  (RepeatSelectOption &&
                    reapeatprevSelectOption &&
                    !reapeatprevSelectOption.includes(prevSelectOption))) && (
                  <div style={{ borderRadius: '50%' }}>
                    <Buttons
                      variant="ghost"
                      size="sm"
                      onClick={handleRight}
                      className="h-10 w-[3.75rem] rounded-3xl px-4 py-2 transition-all duration-200 hover:scale-110"
                      style={{
                        background: `linear-gradient(90deg, rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.6) 7.25%, rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.15) 84.803%)`,
                        border: `1px solid ${primaryColor}`,
                        boxShadow: `0px 0px 15.9542px 0px rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.3)`,
                      }}
                    >
                      <ChevronRight className="!h-4 !w-4 text-white" />
                    </Buttons>
                  </div>
                )}
            </div>

            {/* Background Ellipse */}
            <div className="pointer-events-none absolute left-[11.81rem] top-[2.435rem] h-[43.427rem] w-[28.455rem] mix-blend-hard-light">
              <div className="absolute bottom-[-61.407%] left-[-93.717%] right-[-93.717%] top-[-61.407%]">
                <svg
                  className="block h-full w-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 1309 1549"
                >
                  <g
                    filter={`url(#filter0_f_interaction_${primaryColor.replace(
                      '#',
                      '',
                    )})`}
                    id="Ellipse 7189"
                    opacity="0.1"
                    style={{ mixBlendMode: 'hard-light' }}
                  >
                    <ellipse
                      cx="654.639"
                      cy="774.418"
                      fill={primaryColor}
                      rx="227.639"
                      ry="347.418"
                    />
                  </g>
                  <defs>
                    <filter
                      colorInterpolationFilters="sRGB"
                      filterUnits="userSpaceOnUse"
                      height="1548.19"
                      id={`filter0_f_interaction_${primaryColor.replace(
                        '#',
                        '',
                      )}`}
                      width="1308.63"
                      x="0.325104"
                      y="0.325104"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        mode="normal"
                        result="shape"
                      />
                      <feGaussianBlur
                        result="effect1_foregroundBlur_39_61"
                        stdDeviation="213.337"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Inset Shadow Border */}
            <div
              className="pointer-events-none absolute inset-0 rounded-[2.375rem]"
              style={{
                boxShadow: `0px 0px 11px 0px inset rgba(${hexToRgb(
                  primaryColor,
                )}, 0.3), 0px 0px 20px 0px rgba(${hexToRgb(
                  primaryColor,
                )}, 0.2)`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractionFutureTheme;
